import { PARTICIPANT_GRID_MAX_VISIBLE_TILES } from "./participant-grid-capacity";

const AUTO_LAYOUT_MODE = "auto";
const GRID_LAYOUT_MODE = "grid";
const SPOTLIGHT_LAYOUT_MODE = "spotlight";
const MAX_GRID_COLUMNS = 3;

function normalizeMaxVisibleGridTiles(maxVisibleGridTiles) {
  if (!Number.isFinite(maxVisibleGridTiles)) {
    return PARTICIPANT_GRID_MAX_VISIBLE_TILES;
  }

  return Math.min(PARTICIPANT_GRID_MAX_VISIBLE_TILES, Math.max(1, Math.floor(maxVisibleGridTiles)));
}

function createSlot({ index, role, emphasis, placement, row, column }) {
  return { index, role, emphasis, placement, row, column };
}

function buildBalancedGridRows(participantCount) {
  const columnCount = Math.min(MAX_GRID_COLUMNS, participantCount);
  const rowCount = Math.ceil(participantCount / columnCount);
  const baseRowSize = Math.floor(participantCount / rowCount);
  const oversizedRowCount = participantCount % rowCount;

  return Array.from({ length: rowCount }, (_, rowIndex) => baseRowSize + (rowIndex < oversizedRowCount ? 1 : 0));
}

function buildSlots(rows, slotConfig) {
  let slotIndex = 0;

  return rows.flatMap((rowSize, rowIndex) =>
    Array.from({ length: rowSize }, (_, columnIndex) =>
      createSlot(slotConfig({ index: slotIndex++, row: rowIndex, column: columnIndex }))
    )
  );
}

function resolveGridVariant(rows) {
  if (rows.length === 1 && rows[0] === 3) {
    return "equal-row";
  }

  if (rows.length === 2 && rows[0] === 3 && rows[1] === 2) {
    return "mosaic";
  }

  return "balanced-grid";
}

function normalizeParticipantCount(participantCount) {
  if (!Number.isFinite(participantCount)) {
    return 0;
  }

  return Math.floor(Math.max(0, participantCount));
}

function getEmptyLayout(mode) {
  return {
    mode,
    variant: "empty",
    rows: [],
    slotCount: 0,
    slots: []
  };
}

function getSpotlightLayout(participantCount, { usePinnedTwoParticipantStrip = false } = {}) {
  if (participantCount === 1) {
    return {
      mode: SPOTLIGHT_LAYOUT_MODE,
      variant: "single",
      rows: [1],
      slotCount: 1,
      slots: [createSlot({ index: 0, role: "stage", emphasis: "primary", placement: "center", row: 0, column: 0 })]
    };
  }

  if (participantCount === 2) {
    if (usePinnedTwoParticipantStrip) {
      return {
        mode: SPOTLIGHT_LAYOUT_MODE,
        variant: "stage-with-strip",
        rows: [1, 1],
        slotCount: 2,
        slots: [
          createSlot({ index: 0, role: "stage", emphasis: "primary", placement: "center", row: 0, column: 0 }),
          createSlot({ index: 1, role: "tile", emphasis: "secondary", placement: "grid", row: 1, column: 0 })
        ]
      };
    }

    return {
      mode: SPOTLIGHT_LAYOUT_MODE,
      variant: "stage-with-pip",
      rows: [1, 1],
      slotCount: 2,
      slots: [
        createSlot({ index: 0, role: "stage", emphasis: "primary", placement: "center", row: 0, column: 0 }),
        createSlot({ index: 1, role: "pip", emphasis: "secondary", placement: "bottom-right", row: 1, column: 0 })
      ]
    };
  }

  const secondaryRows = buildBalancedGridRows(participantCount - 1);

  return {
    mode: SPOTLIGHT_LAYOUT_MODE,
    variant: "stage-with-strip",
    rows: [1, ...secondaryRows],
    slotCount: participantCount,
    slots: [
      createSlot({ index: 0, role: "stage", emphasis: "primary", placement: "center", row: 0, column: 0 }),
      ...buildSlots(secondaryRows, ({ index, row, column }) => ({
        index: index + 1,
        role: "tile",
        emphasis: "secondary",
        placement: "grid",
        row: row + 1,
        column
      }))
    ]
  };
}

function getGridLayout(participantCount, maxVisibleGridTiles = PARTICIPANT_GRID_MAX_VISIBLE_TILES) {
  const visibleGridTileLimit = normalizeMaxVisibleGridTiles(maxVisibleGridTiles);
  const visibleParticipantCount = Math.min(participantCount, visibleGridTileLimit);
  const rows = buildBalancedGridRows(visibleParticipantCount);

  return {
    mode: GRID_LAYOUT_MODE,
    variant: resolveGridVariant(rows),
    rows,
    slotCount: visibleParticipantCount,
    slots: buildSlots(rows, ({ index, row, column }) => ({
      index,
      role: "tile",
      emphasis: "equal",
      placement: "grid",
      row,
      column
    }))
  };
}

export function resolveParticipantLayoutMode({ participantCount = 0, mode = AUTO_LAYOUT_MODE } = {}) {
  const normalizedParticipantCount = normalizeParticipantCount(participantCount);

  if (mode === GRID_LAYOUT_MODE || mode === SPOTLIGHT_LAYOUT_MODE) {
    return mode;
  }

  return normalizedParticipantCount <= 2 ? SPOTLIGHT_LAYOUT_MODE : GRID_LAYOUT_MODE;
}

export function getParticipantLayout({
  participantCount = 0,
  mode = AUTO_LAYOUT_MODE,
  maxVisibleGridTiles = PARTICIPANT_GRID_MAX_VISIBLE_TILES,
  usePinnedTwoParticipantStrip = false
} = {}) {
  const safeParticipantCount = normalizeParticipantCount(participantCount);
  const resolvedMode = resolveParticipantLayoutMode({
    participantCount: safeParticipantCount,
    mode
  });

  if (safeParticipantCount === 0) {
    return getEmptyLayout(resolvedMode);
  }

  if (resolvedMode === SPOTLIGHT_LAYOUT_MODE) {
    return getSpotlightLayout(safeParticipantCount, { usePinnedTwoParticipantStrip });
  }

  return getGridLayout(safeParticipantCount, maxVisibleGridTiles);
}
